/**
 * Grouped ("cluster") view for the Search Terms/Negatives tab, plus the
 * shared recommendation table used by BOTH the grouped and flat views.
 *
 * Art §F / client-UI item 5: cluster recommendations by category with a
 * tri-state select-all per cluster so a whole cluster can be applied in
 * ~3 clicks (cluster checkbox → Apply selected negatives → confirm).
 *
 * Selection state stays in the parent (SearchTermsNegativesTab) — this file
 * only reports toggles back through callbacks so the selected-count card,
 * apply payload, and rejected-ids semantics are identical in both views.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { SortableTh, type SortDir } from '@/components/ui/data-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { SearchTermNegativeRecommendation } from '@/services/googleAdsWeeklyService';
import { ConvertedBadge } from './ConvertedBadge';
import { formatConversionCount, isConvertingRec } from './convertingSearchTerm';

type Rec = SearchTermNegativeRecommendation;

export const CATEGORY_COPY: Record<string, { label: string; className: string }> = {
  competitor_or_other_brand: {
    label: 'Competitor/brand',
    className: 'border-coral/30 bg-coral-light text-coral-dark',
  },
  informational: {
    label: 'Informational',
    className: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
  },
  diy_or_self_help: {
    label: 'DIY/self-help',
    className: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
  },
  job_seeker: {
    label: 'Jobs',
    className: 'border-destructive/30 bg-destructive/5 text-destructive',
  },
  wrong_vertical: {
    label: 'Wrong vertical',
    className: 'border-destructive/30 bg-destructive/5 text-destructive',
  },
  geo_mismatch: {
    label: 'Geo mismatch',
    className: 'border-coral/30 bg-coral-light text-coral-dark',
  },
  low_intent: {
    label: 'Low intent',
    className: 'border-border bg-warm-bg text-muted-foreground',
  },
};

/** Cluster order: safety-relevant categories first (Art §F). */
const CATEGORY_ORDER = [
  'geo_mismatch',
  'competitor_or_other_brand',
  'wrong_vertical',
  'diy_or_self_help',
  'job_seeker',
  'informational',
  'low_intent',
];

export function formatUsdMicros(value: number | null | undefined): string {
  if (value == null) return '—';
  return `$${(value / 1_000_000).toFixed(2)}`;
}

export function categoryBadge(category: string) {
  const copy = CATEGORY_COPY[category] ?? {
    label: category.replaceAll('_', ' '),
    className: 'border-border bg-warm-bg text-foreground',
  };
  return (
    <Badge variant="outline" className={copy.className}>
      {copy.label}
    </Badge>
  );
}

/** Stable module-scope empty set so the default prop never re-creates. */
const EMPTY_KEYS: Set<string> = new Set();

/**
 * Durable identity of an applied negative — ``negative_keyword|match_type``
 * (lowercased). This is the actual applied unit and how the backend shared
 * set dedupes, so applied-state survives recommendation-id churn on refetch.
 */
export function appliedKey(
  rec: Pick<Rec, 'negative_keyword' | 'match_type'>,
): string {
  return `${rec.negative_keyword.toLowerCase()}|${rec.match_type}`;
}

/** Persistent "already applied" marker shown in place of the row checkbox. */
function AppliedBadge() {
  return (
    <Badge
      variant="outline"
      className="gap-1 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400"
      title={
        'This negative already exists for this account — applied through ' +
        'Core, or found live in the campaign/ad-group/shared-set negatives ' +
        '(including an existing broader match type that covers it).'
      }
    >
      <Check className="h-3 w-3" />
      Applied
    </Badge>
  );
}

/**
 * SERP verdict on a competitor/brand flag ("brand pattern + SERP-validated").
 * Renders nothing when validation never ran (flag off / not a competitor
 * flag) — an absent badge means "not attempted", which must stay visually
 * distinct from "attempted and unavailable".
 */
const SERP_COPY: Record<
  string,
  { label: string; className: string; title: string }
> = {
  confirmed: {
    label: 'SERP ✓ brand',
    className: 'border-teal/40 bg-teal-light text-teal-dark',
    title:
      'SERP-validated: a non-client domain matching this brand dominates ' +
      'the search results — the signature of a real brand/competitor. ' +
      'Details in the evidence column.',
  },
  not_confirmed: {
    label: 'SERP: no brand found',
    className: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300',
    title:
      'The SERP lookup ran and no matching brand dominates the results — ' +
      'this may be a false positive of the brand pattern. The flag is kept; ' +
      'you decide.',
  },
  unavailable: {
    label: 'SERP n/a',
    className: 'border-border bg-warm-bg text-muted-foreground',
    title:
      'SERP validation ran but could not get an answer (lookup failed or ' +
      'over the per-review cap). The flag is unaffected.',
  },
};

export function serpValidationBadge(rec: Pick<Rec, 'serp_validation'>) {
  const copy = rec.serp_validation ? SERP_COPY[rec.serp_validation] : undefined;
  if (!copy) return null;
  return (
    <Badge variant="outline" className={copy.className} title={copy.title}>
      {copy.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Bulk select / clear, shared by the flat header, the grouped Select all and
// each cluster checkbox
// ---------------------------------------------------------------------------

interface BulkSelectState {
  checked: boolean | 'indeterminate';
  /** True when a click clears (every selectable row is already ticked). */
  clearing: boolean;
  disabled: boolean;
  selectableCount: number;
  selectedCount: number;
  /** What a click does, given the checkbox's next state. */
  toggle: (checked: boolean | 'indeterminate') => void;
}

/**
 * Select all never picks a converting term: those are ticked one by one.
 * Clear all clears every open row it governs, a hand-ticked converting row
 * included (review of #3045, finding D: it used to stay ticked, and so stay
 * in the apply).
 */
function bulkSelectState(
  rows: Rec[],
  selectedIds: Set<string>,
  appliedKeys: Set<string>,
  onToggleRows: (ids: string[], select: boolean) => void,
): BulkSelectState {
  const openRows = rows.filter((rec) => !appliedKeys.has(appliedKey(rec)));
  const selectableRows = openRows.filter((rec) => !isConvertingRec(rec));
  const selectedCount = selectableRows.filter((rec) =>
    selectedIds.has(rec.id),
  ).length;
  const anyOpenSelected = openRows.some((rec) => selectedIds.has(rec.id));
  const allSelected =
    selectableRows.length > 0 && selectedCount === selectableRows.length;
  const clearing = anyOpenSelected && selectedCount === selectableRows.length;
  return {
    checked: allSelected ? true : anyOpenSelected ? 'indeterminate' : false,
    clearing,
    disabled: !selectableRows.length && !anyOpenSelected,
    selectableCount: selectableRows.length,
    selectedCount,
    toggle: (checked) => {
      const select = checked === true && !clearing;
      onToggleRows(
        (select ? selectableRows : openRows).map((rec) => rec.id),
        select,
      );
    },
  };
}

// ---------------------------------------------------------------------------
// Shared recommendation table (flat view body + each cluster section body)
// ---------------------------------------------------------------------------

export interface RecommendationTableSort {
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
}

export interface RecommendationTableProps {
  /** Rows rendered in the body (the current page in the flat view). */
  rows: Rec[];
  /**
   * Rows the header checkbox governs. Defaults to ``rows``; the flat view
   * passes ALL filtered rows so select-all spans every page.
   */
  selectionRows?: Rec[];
  selectedIds: Set<string>;
  /**
   * Keys (``appliedKey``) already applied this session. Applied rows render an
   * "Applied" badge instead of a checkbox and are excluded from select-all.
   */
  appliedKeys?: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleRows: (ids: string[], select: boolean) => void;
  sort: RecommendationTableSort;
  widths: Record<string, number>;
  onResizeStart: (key: string, event: React.PointerEvent<HTMLElement>) => void;
  /** Extra classes for the scroll container (e.g. max-h-[60vh] flat view). */
  containerClassName?: string;
  emptyMessage?: string;
}

/** The full 14-column recommendations table, shared by both views. */
export function RecommendationTable({
  rows,
  selectionRows,
  selectedIds,
  appliedKeys = EMPTY_KEYS,
  onToggleRow,
  onToggleRows,
  sort,
  widths,
  onResizeStart,
  containerClassName,
  emptyMessage = 'No negative keyword recommendations.',
}: RecommendationTableProps) {
  // Select-all only governs rows that are still selectable (not yet applied)
  // and never picks a converting term; Clear all clears them too.
  const bulk = bulkSelectState(
    selectionRows ?? rows,
    selectedIds,
    appliedKeys,
    onToggleRows,
  );
  const { sortKey, sortDir, onSort } = sort;

  return (
    <Table containerClassName={containerClassName}>
      <TableHeader className="sticky top-0 z-10 bg-warm-bg [&_th]:bg-warm-bg">
        <TableRow>
          <TableHead className="w-[72px]">
            <div className="flex items-center gap-2">
              <Checkbox
                aria-label={
                  bulk.clearing
                    ? 'Clear all listed recommendations'
                    : 'Select all listed recommendations'
                }
                checked={bulk.checked}
                disabled={bulk.disabled}
                onCheckedChange={bulk.toggle}
              />
              <span>Use</span>
            </div>
          </TableHead>
          <SortableTh
            sortKey="negative_keyword"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            resizable
            width={widths.negative_keyword}
            onResize={onResizeStart}
          >
            Negative
          </SortableTh>
          <SortableTh
            sortKey="category"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
          >
            Category
          </SortableTh>
          <TableHead className="min-w-[260px]">Evidence</TableHead>
          <SortableTh
            sortKey="search_term"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            resizable
            width={widths.search_term}
            onResize={onResizeStart}
          >
            Search term
          </SortableTh>
          <SortableTh
            sortKey="keyword"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            resizable
            width={widths.keyword}
            onResize={onResizeStart}
          >
            Keyword
          </SortableTh>
          <SortableTh
            sortKey="campaign"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            resizable
            width={widths.campaign}
            onResize={onResizeStart}
          >
            Campaign
          </SortableTh>
          <SortableTh
            sortKey="ad_group"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            resizable
            width={widths.ad_group}
            onResize={onResizeStart}
          >
            Ad group
          </SortableTh>
          <SortableTh
            sortKey="spend"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            className="text-right"
          >
            Spend
          </SortableTh>
          <SortableTh
            sortKey="clicks"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            className="text-right"
          >
            Clicks
          </SortableTh>
          <SortableTh
            sortKey="impressions"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            className="text-right"
          >
            Impr.
          </SortableTh>
          <SortableTh
            sortKey="conversions"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            className="text-right"
          >
            Conv.
          </SortableTh>
          <SortableTh
            sortKey="cpc"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            className="text-right"
          >
            CPC
          </SortableTh>
          <SortableTh
            sortKey="cpa"
            currentKey={sortKey}
            dir={sortDir}
            onSort={onSort}
            className="text-right"
          >
            CPA
          </SortableTh>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? (
          rows.map((rec) => (
            <RecommendationRow
              key={rec.id}
              rec={rec}
              selected={selectedIds.has(rec.id)}
              applied={appliedKeys.has(appliedKey(rec))}
              onToggle={() => onToggleRow(rec.id)}
            />
          ))
        ) : (
          <TableRow>
            <TableCell
              colSpan={14}
              className="py-6 text-center text-sm text-muted-foreground"
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

function RecommendationRow({
  rec,
  selected,
  applied,
  onToggle,
}: {
  rec: Rec;
  selected: boolean;
  applied: boolean;
  onToggle: () => void;
}) {
  return (
    <TableRow
      className={
        applied
          ? 'border-l-2 border-l-emerald-400 bg-emerald-50 dark:bg-emerald-950/40/40 opacity-80'
          : undefined
      }
    >
      <TableCell>
        {applied ? (
          <AppliedBadge />
        ) : (
          <Checkbox checked={selected} onCheckedChange={onToggle} />
        )}
      </TableCell>
      <TableCell>
        <div className="font-medium">{rec.negative_keyword}</div>
        <div className="text-xs text-muted-foreground">{rec.match_type}</div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {categoryBadge(rec.category)}
          {serpValidationBadge(rec)}
          <ConvertedBadge rec={rec} />
        </div>
      </TableCell>
      <TableCell className="min-w-[260px]">
        <div className="text-sm">{rec.reason}</div>
        <div className="mt-1 text-xs text-muted-foreground">{rec.evidence}</div>
        {rec.coverage_note ? (
          <div className="mt-1 text-xs text-muted-foreground" data-testid="coverage-note">
            {rec.coverage_note}
          </div>
        ) : null}
      </TableCell>
      <TableCell className="min-w-[220px]">{rec.search_term}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {rec.metrics.triggering_keyword ?? '—'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {rec.metrics.campaign_name ?? '—'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {rec.metrics.ad_group_name ?? '—'}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatUsdMicros(rec.metrics.cost_micros)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {rec.metrics.clicks}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {rec.metrics.impressions}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatConversionCount(rec.metrics.conversions)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatUsdMicros(rec.metrics.cpc_micros)}
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {formatUsdMicros(rec.metrics.cpa_micros)}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Grouped (cluster) view
// ---------------------------------------------------------------------------

export interface SearchTermClustersProps {
  /** Rule-filtered, sorted rows (same pipeline as the flat view, unpaged). */
  rows: Rec[];
  selectedIds: Set<string>;
  /** Keys (``appliedKey``) already applied this session. */
  appliedKeys: Set<string>;
  onToggleRow: (id: string) => void;
  onToggleRows: (ids: string[], select: boolean) => void;
  sort: RecommendationTableSort;
  widths: Record<string, number>;
  onResizeStart: (key: string, event: React.PointerEvent<HTMLElement>) => void;
  emptyMessage: string;
}

export function SearchTermClusters({
  rows,
  selectedIds,
  appliedKeys,
  onToggleRow,
  onToggleRows,
  sort,
  widths,
  onResizeStart,
  emptyMessage,
}: SearchTermClustersProps) {
  // Sections default OPEN; we track only the explicitly collapsed ones so a
  // data refresh never surprises the operator by closing sections.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const clusters = useMemo(() => {
    const byCategory = new Map<string, Rec[]>();
    rows.forEach((rec) => {
      const list = byCategory.get(rec.category);
      if (list) list.push(rec);
      else byCategory.set(rec.category, [rec]);
    });
    const known = CATEGORY_ORDER.filter((category) => byCategory.has(category));
    const unknown = [...byCategory.keys()]
      .filter((category) => !CATEGORY_ORDER.includes(category))
      .sort();
    return [...known, ...unknown].map((category) => {
      const clusterRows = byCategory.get(category) ?? [];
      return {
        category,
        rows: clusterRows,
        spendMicros: clusterRows.reduce(
          (sum, rec) => sum + rec.metrics.cost_micros,
          0,
        ),
      };
    });
  }, [rows]);

  const toggleCollapsed = (category: string, open: boolean) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  if (!rows.length) {
    return (
      <div className="rounded border border-border py-6 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  // Select-all / tri-state ignore already-applied rows and converting rows
  // (converting terms are only ever ticked one by one); Clear all clears a
  // hand-ticked converting row too.
  const bulk = bulkSelectState(rows, selectedIds, appliedKeys, onToggleRows);
  const openTotal = rows.filter((rec) => !appliedKeys.has(appliedKey(rec))).length;
  const appliedTotal = rows.length - openTotal;
  const convertingTotal = openTotal - bulk.selectableCount;

  return (
    <div className="space-y-3">
      <label className="flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground">
        <Checkbox
          aria-label={
            bulk.clearing
              ? 'Clear all filtered recommendations'
              : 'Select all filtered recommendations'
          }
          checked={bulk.checked}
          disabled={bulk.disabled}
          onCheckedChange={bulk.toggle}
        />
        Select all {bulk.selectableCount} filtered term
        {bulk.selectableCount === 1 ? '' : 's'}
        {appliedTotal > 0 && (
          <span className="text-emerald-700 dark:text-emerald-400">· {appliedTotal} applied</span>
        )}
        {convertingTotal > 0 && (
          <span data-testid="converting-skip-note">
            · {convertingTotal} converted, tick {convertingTotal === 1 ? 'it' : 'those'} one by one
          </span>
        )}
      </label>

      {clusters.map((cluster) => {
        const open = !collapsed.has(cluster.category);
        const clusterBulk = bulkSelectState(
          cluster.rows,
          selectedIds,
          appliedKeys,
          onToggleRows,
        );
        const clusterApplied = cluster.rows.filter((rec) =>
          appliedKeys.has(appliedKey(rec)),
        ).length;
        // Every ticked row in the cluster, a hand-ticked converting one too.
        const clusterSelected = cluster.rows.filter(
          (rec) => !appliedKeys.has(appliedKey(rec)) && selectedIds.has(rec.id),
        ).length;
        const clusterFullyApplied =
          cluster.rows.length > 0 && clusterApplied === cluster.rows.length;
        const label =
          CATEGORY_COPY[cluster.category]?.label
          ?? cluster.category.replaceAll('_', ' ');
        return (
          <Collapsible
            key={cluster.category}
            open={open}
            onOpenChange={(next) => toggleCollapsed(cluster.category, next)}
            className="rounded border border-border"
          >
            <div className="flex items-center gap-3 bg-warm-bg px-3 py-2">
              <Checkbox
                aria-label={
                  clusterBulk.clearing
                    ? `Clear all ${label} recommendations`
                    : `Select all ${label} recommendations`
                }
                checked={clusterBulk.checked}
                disabled={clusterBulk.disabled}
                onCheckedChange={clusterBulk.toggle}
              />
              <CollapsibleTrigger className="flex min-w-0 flex-1 items-center gap-3 text-left text-sm">
                {categoryBadge(cluster.category)}
                {clusterFullyApplied && (
                  <Badge
                    variant="outline"
                    className="gap-1 border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400"
                  >
                    <Check className="h-3 w-3" />
                    All applied
                  </Badge>
                )}
                <span className="text-muted-foreground">
                  {cluster.rows.length} term
                  {cluster.rows.length === 1 ? '' : 's'}
                  {clusterSelected > 0 && (
                    <span className="ml-1">({clusterSelected} selected)</span>
                  )}
                  {clusterApplied > 0 && !clusterFullyApplied && (
                    <span className="ml-1 text-emerald-700 dark:text-emerald-400">
                      · {clusterApplied} applied
                    </span>
                  )}
                </span>
                <span className="ml-auto font-medium tabular-nums">
                  {formatUsdMicros(cluster.spendMicros)}
                </span>
                <ChevronDown
                  className={`h-4 w-4 flex-none text-muted-foreground transition-transform ${
                    open ? '' : '-rotate-90'
                  }`}
                />
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent className="border-t border-border">
              <RecommendationTable
                rows={cluster.rows}
                selectedIds={selectedIds}
                appliedKeys={appliedKeys}
                onToggleRow={onToggleRow}
                onToggleRows={onToggleRows}
                sort={sort}
                widths={widths}
                onResizeStart={onResizeStart}
              />
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
